import React, { useState, useEffect } from 'react';
import {
  Case,
  QueueConfiguration,
  Feedback,
  OpinionCategoryLabels,
  getCaseAction,
  trackEvent,
  getFilterOnState,
  CasePoints,
  getCasePoints,
  trackException,
  TenantFull,
  getTenantInfo,
  CasePointsInformation
} from 'services';
import {
  StackItem,
  Stack,
  Text,
  IStackStyles,
  Dialog,
  MessageBar,
  MessageBarType,
  getTheme,
  Separator,
  SpinnerSize,
  ITextStyles
} from '@fluentui/react';
import EvidenceStats from 'components/layout/evidence/components/header/EvidenceStats';
import { CaseCard, ZoomModal, StyledTag, TableComponent } from 'components/shared';
import { OverturnForm } from './LookupOverturnForm';
import { CaseAction } from 'services';
import { Spinner } from '@fluentui/react';
import { getCaseEvidenceAndContext } from '../review/ReviewOpinionForm';
import { EvidenceTable } from '../layout';
import { SalusPrimaryButton } from '../shared/SalusPrimaryButton';
import { useHistory } from 'react-router';
import { AxiosError } from 'axios';

const ENABLE_ALL_SUBJECT_IMAGE_FILTER_SETTINGS = true;

const minOverturFormWidth = 340;
const columnStyle: IStackStyles = {
  root: {
    minWidth: '350px'
  }
};

export interface LookupComponentProps {
  config: QueueConfiguration;
  case: Case;
  feedbacks: Feedback[];
}

interface NonModeratorArray {
  'Reported by': string;
  'Reported trait': JSX.Element[];
  'User comments': string;
  Submitted: string;
}

interface ModeratorArray {
  'Deciding agent': string;
  'Reported trait': JSX.Element[];
  'Agent comments': string;
  Submitted: string;
  'Actions taken': JSX.Element[];
}

interface PointsArray {
  Strikes: string;
  'Account Total Strikes': string;
  'Max Strikes': string;
  'Stacking Expiration Date': string;
}

function getTraits(opinionCategoryLabels: OpinionCategoryLabels): JSX.Element[] {
  if (opinionCategoryLabels) {
    const result: JSX.Element[] = [];
    Object.keys(opinionCategoryLabels).forEach(key => {
      opinionCategoryLabels[key] &&
        opinionCategoryLabels[key].forEach(label => {
          result.push(<StyledTag className="mr-1 mb-1" key={label} tag={label} />);
        });
    });
    return result;
  } else {
    return [
      <Text key="none" variant="medium">
        None
      </Text>
    ];
  }
}

function fillUserReportsTable(nonModeratorFeedback: Feedback[]): JSX.Element[] {
  const nonModeratorArray: NonModeratorArray[] = [];
  nonModeratorFeedback &&
    nonModeratorFeedback
      .sort((a, b) => new Date(b.feedbackDateTime).getTime() - new Date(a.feedbackDateTime).getTime())
      .map(feedback =>
        nonModeratorArray.push({
          'Reported by': feedback.source.sourceId,
          'Reported trait': getTraits(feedback.opinion.opinionCategoryLabels),
          'User comments': feedback.opinion.comment,
          Submitted: new Date(feedback.feedbackDateTime).toLocaleString()
        })
      );
  // @ts-ignore
  return [<TableComponent key="userReportTable" clickable={false} data={nonModeratorArray} />];
}

function fillEnforcementHistoryTable(
  moderatorFeedback: Feedback[],
  caseActions: CaseAction[] | undefined
): JSX.Element[] {
  const moderatorArray: ModeratorArray[] = [];

  moderatorFeedback &&
    moderatorFeedback
      .sort((a, b) => new Date(b.feedbackDateTime).getTime() - new Date(a.feedbackDateTime).getTime())
      .map(feedback =>
        moderatorArray.push({
          'Deciding agent': feedback.source.sourceId,
          'Reported trait': getTraits(feedback.opinion.opinionCategoryLabels),
          'Agent comments': feedback.opinion.comment,
          Submitted: new Date(feedback.feedbackDateTime).toLocaleString(),
          'Actions taken':
            (caseActions &&
              caseActions
                .filter(action => action.feedbackId === feedback.id)
                .flatMap(action =>
                  action.actions.map(action => (
                    <Text key={action.action}>
                      {action.action}
                      <br />
                    </Text>
                  ))
                )) ||
            []
        })
      );
  // @ts-ignore
  return [<TableComponent key="enforcementHistoryTable" clickable={false} data={moderatorArray} />];
}

function fillStackingEnforcementsTable(
  points: CasePointsInformation[] | undefined,
  stackingExpiration: Date | undefined
): JSX.Element[] {
  const pointsArray: PointsArray[] = [];

  points &&
    stackingExpiration &&
    points.map(row =>
      pointsArray.push({
        Strikes: `${row.casePointsValue}`,
        'Account Total Strikes': `${row.activeTotalPointsAfterAction}`,
        'Max Strikes': `${row.maxPoints}`,
        'Stacking Expiration Date':
          points.length === 1 || (points.length > 1 && pointsArray.length === 1)
            ? new Date(stackingExpiration).toLocaleString()
            : ''
      })
    );

  // @ts-ignore
  return [<TableComponent key="stackingEnforcementsTable" clickable={false} data={pointsArray} />];
}

export const LookupComponent: React.FunctionComponent<LookupComponentProps> = props => {
  const { palette } = getTheme();
  const [isUndoPending, setIsUndoPending] = useState<boolean>(false);
  const [showOverturnForm, setShowOverturnForm] = useState<boolean>(false);
  const [caseActions, setCaseActions] = useState<CaseAction[] | undefined>(undefined);
  const [stackingExpiration, setStackingExpiration] = useState<Date | undefined>(undefined);
  const [casePoints, setCasePoints] = useState<CasePointsInformation[] | undefined>(undefined);
  const [loadingCasePoints, setLoadingCasePoints] = useState<boolean>(true);
  const history = useHistory();
  const moderatorSourceWeight: number = props.config.sourceTypeWeightMappings.Moderator;
  const communitySiftSourceWeight: number | undefined = props.config.sourceTypeWeightMappings.CommunitySift;
  const nonModeratorFeedback = props.feedbacks.filter(
    (item => item && item.sourceWeight && item.sourceWeight < moderatorSourceWeight) ||
      (item => item.sourceWeight && communitySiftSourceWeight && item.sourceWeight < communitySiftSourceWeight)
  );
  const moderatorFeedback = props.feedbacks.filter(
    (item => item && item.sourceWeight && item.sourceWeight >= moderatorSourceWeight) ||
      (item => item.sourceWeight && communitySiftSourceWeight && item.sourceWeight >= communitySiftSourceWeight)
  );
  const canBeOverturned: boolean =
    (caseActions &&
      caseActions.some(items =>
        items.actions.some(
          action => action.undoCategorizedTraits !== undefined && Object.keys(action.undoCategorizedTraits).length !== 0
        )
      )) ||
    false;

  const container = { root: { backgroundColor: palette.neutralLighterAlt } };
  const tableStyle = { root: { backgroundColor: palette.white } };
  const stackingTableTextStyle: ITextStyles = {
    root: {
      textAlign: 'right'
    }
  };

  useEffect(() => {
    getCaseAction(
      props.config.tenant.tenantId,
      props.config.title.titleId,
      props.config.subjectType,
      props.case.subject.subjectId,
      props.case.id
    )
      .then(result => {
        setCaseActions(result);
      })
      .catch((err: AxiosError) => {
        err.message = 'LookupComponent - getCaseAction: ' + err.message;
        trackException(err);
      });

    if (props.case.subject.subjectOwners && props.case.subject.subjectOwners.length > 0) {
      Promise.all([
        getTenantInfo(props.config.tenant.tenantId),
        getCasePoints(
          props.config.tenant.tenantId,
          props.config.title.titleId,
          props.case.subject.subjectOwners[0].userType,
          props.case.subject.subjectOwners[0].userId,
          props.case.id
        )
      ])
        .then((result: [TenantFull, CasePoints]) => {
          if (result[1]) {
            // There will always be a lookback window if there are case points
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const window = result[0].stackingConfigurationByTitleId['Moderation'].lookbackWindowInDays;
            const expirationDate = new Date(result[1].caseClosedDateTime);
            expirationDate.setDate(expirationDate.getDate() + window);

            const pts: CasePointsInformation[] = [];
            if (result[1].caseOverturnedPoints) {
              pts.push(result[1].caseOverturnedPoints);
            }
            pts.push(result[1].caseClosedPoints);

            setCasePoints(pts);
            setStackingExpiration(expirationDate);
          }

          setLoadingCasePoints(false);
        })
        .catch((err: AxiosError) => {
          err.message = 'LookupComponent - StackingEnforcements: ' + err.message;
          trackException(err);
        });
    } else {
      setLoadingCasePoints(false);
    }
  }, [
    props.case.id,
    props.config.subjectType,
    props.config.tenant.tenantId,
    props.config.title.titleId,
    props.case.subject.subjectId,
    props.case.subject.subjectOwners
  ]);

  const reviewCase = () => {
    trackEvent('ReviewButton', { component: 'LookupComponent', eventType: 'click', queueId: props.config.id });
    history.push({
      pathname: '/review',
      search: `?id=${props.config.id}}`,
      state: { passedQueueData: props.config, caseId: props.case.id }
    });
  };

  const [, selectedEvidence, selectedContext] = getCaseEvidenceAndContext(props.case);

  const subjectImagePreferences = ENABLE_ALL_SUBJECT_IMAGE_FILTER_SETTINGS
    ? getFilterOnState(props.config.uxPreferences.subjectImage)
    : props.config.uxPreferences.subjectImage;

  const [zoomItem, setZoomItem] = useState<Case | undefined>(undefined);
  return (
    <>
      <Dialog
        hidden={!showOverturnForm}
        modalProps={{
          isBlocking: false,
          overlay: {
            isDarkThemed: true
          }
        }}
        onDismiss={(): void => setShowOverturnForm(false)}
        minWidth={minOverturFormWidth}
        styles={{
          root: {
            selectors: {
              'div .ms-Dialog-button--close': {
                display: 'inline-block'
              }
            }
          }
        }}
      >
        <OverturnForm
          actions={caseActions || []}
          case={props.case}
          config={props.config}
          onSubmit={(): void => {
            setShowOverturnForm(false);
            setIsUndoPending(true);
          }}
        />
      </Dialog>
      <ZoomModal
        config={props.config}
        case={zoomItem}
        isOpen={zoomItem !== undefined}
        onDismiss={(): void => setZoomItem(undefined)}
        settings={{
          ...subjectImagePreferences,
          ...props.config.uxPreferences.subjectJson
        }}
        showCheckbox={false}
      />
      <Stack className="h-100 overflow-hidden position-relative" styles={container}>
        <Text as={'h1'} className="mt-3 mx-5" variant="xxLargePlus">
          Case Information
        </Text>
        <Separator />
        <StackItem className="position-relative h-100">
          <Stack horizontal className="overflow-auto position-absolute h-100 w-100 pb-5">
            <Stack className="text-break w-25 ml-5 mr-3" styles={columnStyle}>
              <Text as={'h2'} className="mb-4" variant="xxLarge">
                Evidence
              </Text>
              <Stack.Item className="mb-3">
                <CaseCard
                  case={props.case}
                  config={props.config}
                  settings={{
                    ...subjectImagePreferences,
                    ...props.config.uxPreferences.subjectJson
                  }}
                  onZoomClick={(item: Case): void => {
                    setZoomItem(item);
                  }}
                />
              </Stack.Item>
              <Stack.Item className="mb-3">
                <div className="font-weight-bold mb-1">
                  <div>Source type</div>
                </div>
                {props.feedbacks[0] && props.feedbacks[0].source.sourceType}
              </Stack.Item>
              <Stack.Item className="mb-3">
                <EvidenceStats feedback={props.feedbacks} />
              </Stack.Item>
              <Stack.Item className="mb-3">
                <Stack.Item className="mb-4">
                  <Text as={'h2'} variant="xxLarge">
                    Details
                  </Text>
                </Stack.Item>
                <Stack.Item className="mb-3">
                  <Text variant="mediumPlus">
                    <div>Case ID</div>
                  </Text>
                  <Text className="mr-1 d-inline-block" variant="medium">
                    <span>{props.case.id.toLowerCase()}</span>
                  </Text>
                </Stack.Item>
                <Stack.Item className="mb-3">
                  <Text variant="mediumPlus">
                    <div>Case status</div>
                  </Text>
                  <Text className="mr-1 d-inline-block" variant="medium">
                    <span>{props.case.status}</span>
                  </Text>
                </Stack.Item>
                <Stack.Item className="mb-3">
                  <Text variant="mediumPlus">
                    <div>Ingestion</div>
                  </Text>
                  <Text className="mr-1 d-inline-block" variant="medium">
                    <span>{new Date(props.case.caseCreationDateTime).toLocaleString()}</span>
                  </Text>
                </Stack.Item>
                <Stack.Item className="mb-3">
                  <Text variant="mediumPlus">
                    <div>Case closed</div>
                  </Text>
                  <Text variant="medium">
                    <span>{new Date(props.case.caseClosedDateTime).toLocaleString()}</span>
                  </Text>
                </Stack.Item>
                {props.case.subject.subjectOwners && (
                  <Stack.Item className="mb-3">
                    <Text variant="mediumPlus">
                      <div>Target user ID</div>
                    </Text>
                    {props.case.subject.subjectOwners.map((owner, index) => {
                      return (
                        <Text key={`${owner.userId}-${index}`} className="mr-1 d-inline-block" variant="medium">
                          <span>
                            {owner.userId}/{owner.userType}/{owner.username}
                          </span>
                        </Text>
                      );
                    })}
                  </Stack.Item>
                )}
                <Stack.Item className="mb-3">
                  <Text variant="mediumPlus">
                    <div>Subject ID</div>
                  </Text>
                  <Text className="mr-1 d-inline-block" variant="medium">
                    <span>{props.case.subject.subjectId}</span>
                  </Text>
                </Stack.Item>
                <Stack.Item className="mb-3">
                  <Text variant="mediumPlus">
                    <div>Queue type</div>
                  </Text>
                  <Text className="mr-1 d-inline-block" variant="medium">
                    <span>{props.case.subject.subjectLabels}</span>
                  </Text>
                </Stack.Item>
                <Stack.Item className="mb-3">
                  <Text variant="mediumPlus">
                    <div>Subject type</div>
                  </Text>
                  <Text variant="medium">
                    <span>{props.case.subject.subjectType}</span>
                  </Text>
                </Stack.Item>
                <Stack.Item className="mb-4">
                  <Text as={'h2'} variant="xxLarge">
                    Reporting Context
                  </Text>
                </Stack.Item>
                <Stack.Item className="mb-3">
                  {selectedEvidence && selectedEvidence.length >= 1 && <EvidenceTable rows={selectedEvidence} />}
                  {props.config.uxPreferences.showReportContext && selectedContext && selectedContext.length >= 1 && (
                    <EvidenceTable rows={selectedContext} />
                  )}
                </Stack.Item>
              </Stack.Item>
            </Stack>
            <Stack className="text-break w-100 mx-3">
              {isUndoPending && (
                <StackItem className="mb-3">
                  <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>
                    Overturn feedback submitted. Refresh page in a couple minutes to load results.
                  </MessageBar>
                </StackItem>
              )}
              <StackItem className="mh-50 rounded mb-3 ms-depth-4" styles={tableStyle}>
                <Stack className="h-100">
                  <StackItem className="px-4 py-3">
                    <Stack horizontal>
                      <StackItem grow>
                        <Text as={'h2'} variant="xxLarge">
                          Enforcement History
                        </Text>
                      </StackItem>
                      <StackItem>
                        {caseActions && !isUndoPending && props.case.status === 'Closed' && canBeOverturned && (
                          <SalusPrimaryButton
                            text="Overturn ruling"
                            className="p-3"
                            onClick={(): void => setShowOverturnForm(true)}
                          />
                        )}
                        {props.case.status === 'Open' && (
                          <SalusPrimaryButton text="Review Case" className="p-3" onClick={reviewCase} />
                        )}
                      </StackItem>
                    </Stack>
                  </StackItem>
                  {caseActions === undefined && <Spinner size={SpinnerSize.large} className="p-5" />}
                  {caseActions && (
                    <StackItem tabIndex={0} className="overflow-auto">
                      {props.feedbacks && fillEnforcementHistoryTable(moderatorFeedback, caseActions)}
                    </StackItem>
                  )}
                </Stack>
              </StackItem>
              <StackItem className="mh-50 rounded mb-3 ms-depth-4" styles={tableStyle}>
                <Stack className="h-100">
                  <StackItem className="px-4 py-3">
                    <Text as={'h2'} variant="xxLarge">
                      User Reports
                    </Text>
                  </StackItem>
                  <StackItem tabIndex={0} className="overflow-auto">
                    {props.feedbacks && fillUserReportsTable(nonModeratorFeedback)}
                  </StackItem>
                </Stack>
              </StackItem>
              <StackItem className="mh-50 rounded ms-depth-4" styles={tableStyle}>
                <Stack className="h-100">
                  <StackItem className="px-4 py-3">
                    <Text as={'h2'} variant="xxLarge">
                      Stacking Enforcements
                    </Text>
                  </StackItem>
                  {loadingCasePoints ? (
                    <Spinner size={SpinnerSize.large} className="p-5" />
                  ) : (
                    <StackItem
                      className="overflow-auto"
                      tabIndex={0}
                      styles={casePoints && stackingExpiration ? stackingTableTextStyle : undefined}
                    >
                      {fillStackingEnforcementsTable(casePoints, stackingExpiration)}
                    </StackItem>
                  )}
                </Stack>
              </StackItem>
            </Stack>
          </Stack>
        </StackItem>
      </Stack>
    </>
  );
};
